// ════════════════════════════════════════════════════════════
//  LimbRig.jsx — THE FIGHT ANIMATIONS: limbs, head and tail
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-20: "Redo the shatterpoof arena in blender and
//  fight animations please <3".
//
//  Fighter.jsx poses the body's ROOT (hop, lean, squash, spin).
//  This file poses what hangs off it — through the kit's rig door
//  (kit v1.1.0 names every bound slot `rig_armL`, `rig_head`, …),
//  so `ieBeingModels.jsx` is never edited: they own the body, we
//  own the pose (ie-being-models §0b).
//
//  WHAT IT ANIMATES, every being, all 36 bodies, no lineage named:
//    run      legs and arms swing in opposite pairs, paced by the
//             same stride phase Fighter's hop uses, so feet and hop agree
//    guard    standing still: arms up a little, a breath, head on the foe
//    windup   the striking arm cocks back, the stance opens
//    strike   a lunge throws the arm (a beast: both forepaws) forward,
//             the back leg drives; a shot points the arm down the aim
//    cast     an Art: arms rise and open (a U'hlti: all the way up),
//             then THROW forward when the Art lands
//    dodge    the Primera: tuck
//    leap     the Dragon's leap: legs tuck, arms sweep back
//    flinch   struck: head snaps back, arms fly out (bigger on a heavy hit)
//    asleep / transcend / ruin — slack, lifted, limp
//    head     always tracks the foe (within a neck's reach)
//    tail     wags with the run and the fight
//
//  ⚑ THE POSE LAW (set, never pile up), and how it is kept here:
//  the kit writes a few slots itself every frame and leaves the rest
//  alone, and a pose that adds onto whatever is there runs away. So
//  every frame, BEFORE the body's own frame, `pre()` (called from
//  Fighter's frame, which always runs first) puts each slot back
//  exactly as it was before we touched it. The kit then writes what
//  it writes onto a clean slot, and our frame (subscribed after the
//  body's) reads that clean value as the base and puts our offset on
//  top. Nothing is ever read back from our own output.
//
//  ⚑ THE JOINT IS MEASURED, NOT ASSUMED (Rush's lesson, made exact):
//  some slots pivot at their own shoulder, some at the BODY ORIGIN
//  half a unit away (a head built that way, turned on its own slot,
//  orbits the feet). So every slot is turned about a joint measured
//  from its own mass: an arm or leg at the end of it nearest the body's
//  top (shoulder, hip), a head or torso at its bottom (neck, waist), a
//  tail at the end nearest the body. Position is carried with the
//  turn, so the limb stays on its joint. One rule, every convention,
//  nothing named.
//
//  Offsets are authored in BODY space (the body faces +Z, Y up):
//    x = swing (− forward / + back) · y = turn · z = for a limb,
//    OUT to its own side (+) or in across the body (−); for the head
//    and torso, a plain roll. Head: x + = nod down, y = look. They are carried into
//  each slot's own parent frame, so a slot buried three groups deep
//  still swings the way the body faces.
// ════════════════════════════════════════════════════════════
import { useLayoutEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// ── dials ────────────────────────────────────────────────────
const MAX_ANGLE = { torso: 0.45, head: 0.95, tail: 1.0, arm: 2.7, leg: 1.7 };   // the most each may turn (radians)
const EASE = 16;              // how fast limbs chase their pose (per second)
const EASE_STRIKE = 34;       // …and when something is thrown
const SWING = 0.75;           // run swing at full speed
const GESTURE = { cast: 0.75, release: 0.4, dodge: 0.45, flinch: 0.32, flinchHeavy: 0.5 };

const LIMB = { armL: 1, armR: 1, legL: 1, legR: 1 };
// which Primera events are a dodge (not "arrive" at a mark, not a leap's "land" — the leap state poses those)
const DODGE_KINDS = { phase: 1, rewind: 1, mark: 1 };
const ZERO = { x: 0, y: 0, z: 0 };
const SLOTS = ["torso", "head", "armL", "armR", "legL", "legR", "tail"];

const _q = new THREE.Quaternion(), _pq = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, "YXZ"), _axis = new THREE.Vector3();
const _inv = new THREE.Matrix4();

// Find the slots and measure each one in its OWN space: where its
// mass sits, and the joint it should turn about. Done once a body is
// on the field (and full size).
const _c = new THREE.Vector3(), _w = new THREE.Vector3();
function massBox(o) {
  // every mesh under the slot, its corners carried into the SLOT's frame
  const box = new THREE.Box3();
  o.updateWorldMatrix(true, true);
  const toSlot = new THREE.Matrix4().copy(o.matrixWorld).invert();
  o.traverse((c) => {
    if (!c.isMesh || !c.geometry) return;
    if (!c.geometry.boundingBox) c.geometry.computeBoundingBox();
    const bb = c.geometry.boundingBox; if (!bb) return;
    const m = new THREE.Matrix4().multiplyMatrices(toSlot, c.matrixWorld);
    for (const x of [bb.min.x, bb.max.x]) for (const y of [bb.min.y, bb.max.y]) for (const z of [bb.min.z, bb.max.z])
      box.expandByPoint(_c.set(x, y, z).applyMatrix4(m));
  });
  return box;
}
// the box's extreme point along a direction (a support point), pulled in a little
function support(box, dir, k) {
  const c = box.getCenter(new THREE.Vector3());
  let h = 0;
  for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z])
    h = Math.max(h, (x - c.x) * dir.x + (y - c.y) * dir.y + (z - c.z) * dir.z);
  return c.addScaledVector(dir, h * k);
}

function capture(host) {
  host.updateWorldMatrix(true, true);
  _inv.copy(host.matrixWorld).invert();
  // the body's own height (fit scale and pose taken back out), for "is this arm a forepaw"
  const hb = new THREE.Box3().setFromObject(host).applyMatrix4(_inv);
  const H = Math.max(0.3, hb.max.y - hb.min.y), y0 = hb.min.y;
  const bodyQ = host.getWorldQuaternion(new THREE.Quaternion());
  const rows = [];
  for (const kind of SLOTS) {
    const o = host.getObjectByName("rig_" + kind);
    if (!o || !o.parent) continue;
    const box = massBox(o);
    if (box.isEmpty()) continue;
    // body UP and body CENTRE, seen from inside the slot
    const slotQ = o.getWorldQuaternion(new THREE.Quaternion());
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(bodyQ).applyQuaternion(slotQ.clone().invert());
    const cS = box.getCenter(new THREE.Vector3());
    const bodyC = hb.getCenter(new THREE.Vector3()).applyMatrix4(host.matrixWorld);
    const toBody = o.worldToLocal(bodyC.clone()).sub(cS).normalize();
    let joint;
    if (kind === "head" || kind === "torso") joint = support(box, up.clone().negate(), kind === "head" ? 0.75 : 0.9);
    else if (kind === "tail") joint = support(box, toBody, 0.9);
    else joint = support(box, up, 0.85);
    const cB = _w.copy(cS).applyMatrix4(o.matrixWorld).applyMatrix4(_inv);
    rows.push({
      kind, o, joint,                                   // joint: in the slot's own space
      savedQ: o.quaternion.clone(), savedP: o.position.clone(), has: false,
      side: kind.endsWith("L") ? 1 : kind.endsWith("R") ? -1 : 0,
      sx: Math.sign(cB.x) || 0,
      low: (cB.y - y0) / H < 0.42,                      // a forepaw, not a hand
      lim: MAX_ANGLE[kind.startsWith("arm") ? "arm" : kind.startsWith("leg") ? "leg" : kind],
      cur: { x: 0, y: 0, z: 0 },
    });
  }
  // side by WHERE the mass is, not by the slot's name — the kit's
  // bodies don't agree on which of L/R sits at +X, and a pair that
  // swings in step instead of opposite reads as a hop, not a run
  for (const pair of [["armL", "armR"], ["legL", "legR"]]) {
    const a = rows.find((r) => r.kind === pair[0]), b = rows.find((r) => r.kind === pair[1]);
    if (a && b && a.sx && b.sx && a.sx !== b.sx) { a.side = a.sx; b.side = b.sx; }
  }
  const arms = rows.filter((r) => r.kind === "armL" || r.kind === "armR");
  const quad = arms.length > 0 && arms.every((r) => r.low);
  const side = {}; for (const r of rows) side[r.kind] = r.side;
  return { rows, quad, side };
}

// Each frame: what every slot should be doing, in body-space radians.
function poseOf(R, f, st, foe, t, gest) {
  const P = {};
  // z on a limb means OUT (+) / IN (−) to its own side; it is turned
  // into a real roll here by the side the limb was measured on
  const add = (k, x = 0, y = 0, z = 0) => {
    const p = P[k] || (P[k] = { x: 0, y: 0, z: 0 });
    p.x += x; p.y += y; p.z += LIMB[k] ? z * (R.side[k] || 0) : z;
  };
  const quad = R.quad;
  const sn = st.sn || 0;                       // 0 still … 1 full run
  const ph = st.phase || 0;
  const s = Math.sin(ph);
  const still = 1 - sn;
  // the striking arm is the one on the body's −X side, whatever the kit named it
  const strikeArm = R.side.armL === -1 ? "armL" : "armR";
  const offArm = strikeArm === "armR" ? "armL" : "armR";

  // ── the run ─────────────────────────────────────────────────
  for (const r of R.rows) {
    if (r.kind === "legL" || r.kind === "legR") add(r.kind, -s * r.side * SWING * sn);
    if (r.kind === "armL" || r.kind === "armR") add(r.kind, s * r.side * SWING * (quad ? 1 : 0.8) * sn);
  }
  add("torso", 0.06 * sn, 0, 0);
  add("tail", 0, Math.sin(t * (4 + sn * 7)) * (0.12 + sn * 0.25), 0);

  // ── guard: standing, ready ──────────────────────────────────
  if (!quad) {
    add("armL", -0.32 * still, 0, 0.12 * still);
    add("armR", -0.32 * still, 0, 0.12 * still);
  }
  const breath = Math.sin(t * 2.2 + (f.side || 0) * 2) * 0.05 * still;
  add("armL", breath, 0, 0); add("armR", breath, 0, 0);
  add("head", -breath * 0.6, 0, 0);

  // ── the fight state ─────────────────────────────────────────
  const m = f.move;
  const prog = (dur) => (dur > 0 ? THREE.MathUtils.clamp(1 - f.stateT / dur, 0, 1) : 1);
  let strike = false;
  switch (f.state) {
    case "windup": {
      const k = m ? prog(m.windup) : 1;
      const e = k * k * (3 - 2 * k);
      if (m && m.kind === "shot") {
        add(strikeArm, -1.1 * e, 0, -0.1 * e);
      } else if (quad) {
        add("armL", 0.55 * e); add("armR", 0.55 * e);        // gather the forepaws under
        add("legL", -0.35 * e); add("legR", -0.35 * e);      // haunches coil
        add("head", 0.22 * e);
      } else {
        add(strikeArm, 1.15 * e, 0, 0.35 * e);              // cock it back
        add(offArm, -0.7 * e, 0, 0.2 * e);                   // the guard hand forward
        add("legL", -0.35 * e); add("legR", 0.35 * e);       // the stance opens
        add("torso", -0.12 * e, 0.18 * e, 0);
        add("head", 0.12 * e);
      }
      break;
    }
    case "active": {
      strike = true;
      if (m && m.kind === "shot") {
        add(strikeArm, -1.55, 0, -0.05); add(offArm, -0.3);
      } else if (quad) {
        add("armL", -1.25); add("armR", -1.25);               // the pounce: both forepaws out
        add("legL", 0.95); add("legR", 0.95);                 // hind legs drive
        add("head", -0.15); add("tail", 0.4);
      } else {
        add(strikeArm, -2.0, 0, -0.12);                         // THROWN (past level: the root leans in)
        add(offArm, 0.75, 0, 0.25);
        add("legL", -0.75); add("legR", 0.85);
        add("torso", 0.28, -0.2, 0);
      }
      break;
    }
    case "recover": {
      const k = m ? prog(m.recover) : 1;
      const e = 1 - k;
      if (m && m.kind === "shot") add(strikeArm, -1.0 * e);
      else if (quad) { add("armL", -0.5 * e); add("armR", -0.5 * e); add("legL", 0.4 * e); add("legR", 0.4 * e); }
      else { add(strikeArm, -0.8 * e); add(offArm, 0.4 * e); add("torso", 0.12 * e); }
      break;
    }
    case "leap": {
      const k = f.leap ? f.leap.t : 0;
      const tuck = Math.sin(Math.min(1, k * 1.3) * Math.PI);
      add("legL", -0.9 * tuck); add("legR", -0.9 * tuck);
      add("armL", quad ? -0.6 * tuck : 0.8 * tuck, 0, 0.5 * tuck);
      add("armR", quad ? -0.6 * tuck : 0.8 * tuck, 0, 0.5 * tuck);
      add("tail", -0.5 * tuck);
      break;
    }
    case "stagger": {
      const k = Math.min(1, f.stateT * 4 + 0.3);
      add("head", -0.4 * k); add("torso", -0.18 * k);
      add("armL", 0.5 * k, 0, 0.7 * k); add("armR", 0.5 * k, 0, 0.7 * k);
      break;
    }
    case "asleep":
      add("head", 0.55); add("armL", 0.15, 0, 0.1); add("armR", 0.15, 0, 0.1); add("tail", 0.3);
      break;
    case "transcend": {
      const k = THREE.MathUtils.clamp(1 - Math.max(0, f.stateT) / 2.6, 0, 1);
      const up = Math.sin(Math.min(1, k * 1.5) * Math.PI * 0.5);
      if (quad) { add("armL", -1.1 * up); add("armR", -1.1 * up); }                       // rearing
      else { add("armL", -0.25 * up, 0, 2.35 * up); add("armR", -0.25 * up, 0, 2.35 * up); }   // a V, over the head
      add("legL", 0.25 * up, 0, 0.15 * up); add("legR", 0.25 * up, 0, 0.15 * up);
      add("head", -0.4 * up);
      break;
    }
    case "ruined": {
      const k = Math.min(1, (st.ruinT || 0) * 1.6);
      add("head", 0.7 * k); add("armL", 0.3 * k, 0, 0.9 * k); add("armR", 0.3 * k, 0, 0.9 * k);
      add("legL", -0.3 * k, 0, 0.3 * k); add("legR", 0.3 * k, 0, 0.3 * k);
      break;
    }
    case "enter": {
      add("armL", -0.5, 0, 0.8); add("armR", -0.5, 0, 0.8);
      break;
    }
    default: break;
  }

  // ── gestures from the engine's events (Arts, dodges, hits) ───
  for (const g of gest) {
    const k = g.t / g.dur;                        // 0 → 1 over the gesture
    const env = Math.sin(Math.min(1, k) * Math.PI);
    if (g.kind === "cast") {
      // summoning: the arms rise out to the sides — to the shoulders for
      // an Art, all the way over the head for a U'hlti
      // held for as long as the cast takes (a level-1 Art waits 3 s for its
      // landing), easing down only in its last 0.15 s if the Art never lands
      const rise = Math.min(1, g.t * 4) * Math.min(1, Math.max(0, (g.dur - g.t) / 0.15));
      if (quad) {                                 // a beast rears: forepaws up, chin up
        add("armL", (g.ult ? -1.3 : -0.8) * rise); add("armR", (g.ult ? -1.3 : -0.8) * rise);
        add("legL", -0.3 * rise); add("legR", -0.3 * rise);
        add("head", (g.ult ? -0.5 : -0.3) * rise); add("tail", -0.5 * rise);
      } else {
        const out = g.ult ? 2.55 : 1.55;
        add("armL", -0.35 * rise, 0, out * rise);
        add("armR", -0.35 * rise, 0, out * rise);
        add("head", (g.ult ? -0.35 : -0.15) * rise);
      }
    } else if (g.kind === "release") {
      strike = true;
      add("armL", -1.35 * env, 0, 0.12 * env); add("armR", -1.35 * env, 0, 0.12 * env);
      add("torso", 0.18 * env); add("head", 0.1 * env);
    } else if (g.kind === "dodge") {
      add("legL", -0.6 * env); add("legR", -0.6 * env);
      add("armL", 0.4 * env, 0, -0.4 * env); add("armR", 0.4 * env, 0, -0.4 * env);
      add("head", 0.25 * env);
    } else if (g.kind === "flinch") {
      const h = g.heavy ? 1.4 : 1;
      const snap = (1 - k) * (1 - k);
      // away from where it came from: g.from is the hit's angle relative to facing
      const back = Math.cos(g.from), sideways = Math.sin(g.from);
      add("head", -0.5 * h * snap * back, -0.4 * h * snap * sideways, 0);
      add("torso", -0.22 * h * snap * back, 0, 0.15 * h * snap * sideways);
      add("armL", 0.45 * h * snap, 0, 0.6 * h * snap); add("armR", 0.45 * h * snap, 0, 0.6 * h * snap);
      strike = true;
    }
  }

  // ── the head finds the foe ──────────────────────────────────
  if (foe && f.state !== "ruined" && f.state !== "asleep") {
    let d = Math.atan2(foe.x - f.x, foe.z - f.z) - f.yaw;
    while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    add("head", 0, THREE.MathUtils.clamp(d, -0.75, 0.75), 0);
  }
  return { P, strike };
}

// ════════════════════════════════════════════════════════════
export default function LimbRig({ host, battle, side, share }) {
  const R = useRef(null);
  const G = useRef({ seq: -1, list: [], tries: 0 });

  // the restore — Fighter's frame calls this BEFORE the body's own frame.
  // Installed on commit (never during render, so a discarded render can't
  // swap it out from under a live rig); unmount leaves every slot clean.
  useLayoutEffect(() => {
    const S = share.current; if (!S) return undefined;
    const restore = () => {
      const r = R.current; if (!r) return;
      for (const row of r.rows) if (row.has) { row.o.quaternion.copy(row.savedQ); row.o.position.copy(row.savedP); row.has = false; }
    };
    S.pre = restore;
    return () => { restore(); if (S.pre === restore) S.pre = null; };
  }, [share]);

  useFrame((_, dtRaw) => {
    const st = share.current; if (!st) return;
    const b = battle.current, f = st.f;
    if (!b || !f || !host.current) return;
    if (!R.current) {
      // the kit names its slots in an effect — give it a few frames, and
      // measure only once the entrance has grown the body to full size
      if ((st.life == null || st.life > 0.95) && G.current.tries++ < 60) {
        const got = capture(host.current);
        if (got.rows.length) {
          R.current = got; G.current.seq = b.seq;
          // dev console handle: window.__limbRig["h01|beb"] — what was found and measured
          if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") (window.__limbRig || (window.__limbRig = {}))[f.line + "|" + f.tier] = got;
        }
      }
      return;
    }
    const r = R.current;
    const dt = st.dt != null ? st.dt : Math.min(dtRaw, 0.05);

    // ── read this being's events since last frame ─────────────
    const gs = G.current;
    if (b.seq < gs.seq) gs.seq = b.seq;              // a new battle
    for (const e of b.events) {
      if (e.seq <= gs.seq) continue;
      if (e.type === "cast" && e.uid === f.uid) gs.list.push({ kind: "cast", ult: !!e.ult, t: 0, dur: Math.max(GESTURE.cast, (e.delay || 0) + 0.3) });
      else if (e.type === "art" && e.uid === f.uid) {
        gs.list = gs.list.filter((g) => g.kind !== "cast");
        gs.list.push({ kind: "release", t: 0, dur: GESTURE.release });
      } else if (e.type === "primera" && e.uid === f.uid && DODGE_KINDS[e.kind]) gs.list.push({ kind: "dodge", t: 0, dur: GESTURE.dodge });
      else if (e.type === "hit" && e.uid === f.uid && !e.tick) {
        const by = e.by != null ? b.sides[1 - f.side].roster.find((x) => x.uid === e.by) : null;
        const from = by ? Math.atan2(by.x - f.x, by.z - f.z) - f.yaw : 0;
        gs.list = gs.list.filter((g) => g.kind !== "flinch");
        gs.list.push({ kind: "flinch", heavy: !!e.heavy, from, t: 0, dur: e.heavy ? GESTURE.flinchHeavy : GESTURE.flinch });
      }
    }
    gs.seq = b.seq;
    for (const g of gs.list) g.t += dt;
    if (gs.list.length) gs.list = gs.list.filter((g) => g.t < g.dur);

    const sd = b.sides[1 - side];
    const foe = sd && sd.active >= 0 ? sd.roster[sd.active] : null;
    const { P, strike } = poseOf(r, f, st, foe, st.t || 0, gs.list);
    const ease = 1 - Math.exp(-dt * (strike ? EASE_STRIKE : EASE));
    // a body shrunk to nothing (a ruin's last frames, an entrance's first)
    // gets no pose — pre() has already left its limbs clean
    if (!(st.life > 0.05)) return;

    // ── write: base (clean, this frame) ← a turn about the measured joint
    const hostObj = host.current;
    for (const row of r.rows) {
      const want = P[row.kind] || ZERO;
      const c = row.cur;
      c.x += (want.x - c.x) * ease; c.y += (want.y - c.y) * ease; c.z += (want.z - c.z) * ease;
      // the base is whatever the slot is NOW — pre() cleaned it, the kit may have written it
      const o = row.o;
      row.savedQ.copy(o.quaternion); row.savedP.copy(o.position); row.has = true;
      if (Math.abs(c.x) + Math.abs(c.y) + Math.abs(c.z) < 1e-4) continue;
      _e.set(c.x, c.y, c.z, "YXZ");
      _q.setFromEuler(_e);
      // the cap: never more than this slot may turn
      const w = Math.min(1, Math.max(-1, _q.w));
      if (2 * Math.acos(Math.abs(w)) > row.lim) {
        _axis.set(_q.x, _q.y, _q.z);
        if (_axis.lengthSq() > 1e-12) { _axis.normalize(); if (w < 0) _axis.negate(); _q.setFromAxisAngle(_axis, row.lim); }
      }
      // body space → the slot's parent space:  P⁻¹·B · q · B⁻¹·P, where B⁻¹·P is
      // the chain of LOCAL rotations from the body down to the slot's parent.
      // Built from quaternions, never decomposed from a matrix — so a squash
      // (non-uniform scale) or a zero scale can't skew or NaN it.
      _pq.identity();
      for (let n = o.parent; n && n !== hostObj; n = n.parent) _pq.premultiply(n.quaternion);
      const toParent = _pq.invert();                       // P⁻¹·B
      _q.premultiply(toParent);
      _q.multiply(toParent.invert());
      // turn about the joint: J = pos + Q·(S⊙joint) ; pos' = J + q·(pos − J) ; Q' = q·Q
      // (the slot's own scale is part of where its joint sits)
      _c.copy(row.joint).multiply(o.scale).applyQuaternion(o.quaternion).add(o.position);
      _w.copy(o.position).sub(_c).applyQuaternion(_q).add(_c);
      o.position.copy(_w);
      o.quaternion.premultiply(_q);
    }
  });
  return null;
}

// exported for the check script
export { poseOf, capture };
